import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { getResponses } from "../lib/api";
import type { Response } from "../lib/types";

interface UseResponsesOptions {
  /**
   * Maximum number of responses to keep client-side. Initial fetch is capped
   * to this many most-recent rows; the realtime feed prepends new inserts and
   * trims from the front when the cap is exceeded. Without a limit, the hook
   * holds the full history (used by stage and facilitator).
   */
  limit?: number;
}

export function useResponses(questionId: string | null, opts: UseResponsesOptions = {}) {
  const [responses, setResponses] = useState<Response[]>([]);
  const { limit } = opts;
  // Per-instance token — see useClusters for the rationale.
  const instanceRef = useRef<string>(crypto.randomUUID());

  const load = useCallback(async () => {
    if (!questionId) { setResponses([]); return; }
    const data = await getResponses(questionId, limit !== undefined ? { limit } : {});
    setResponses(data);
  }, [questionId, limit]);

  useEffect(() => {
    load();
    if (!questionId) return;

    const channel = supabase
      .channel(`responses-${questionId}-${instanceRef.current}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "responses", filter: `question_id=eq.${questionId}` },
        (payload) => {
          const row = payload.new as Response;
          setResponses((prev) => {
            const next = [...prev, row];
            if (limit !== undefined && next.length > limit) {
              return next.slice(next.length - limit);
            }
            return next;
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "responses", filter: `question_id=eq.${questionId}` },
        (payload) => {
          const row = payload.new as Response;
          setResponses((prev) => {
            // Upsert, not just map: with visibility-scoped RLS, anonymous
            // subscribers never receive the INSERT for a pending row — the row
            // first reaches them as the UPDATE that flips it visible. A plain
            // map() would drop it on the floor.
            if (!prev.some((r) => r.id === row.id)) {
              const next = [...prev, row];
              if (limit !== undefined && next.length > limit) {
                return next.slice(next.length - limit);
              }
              return next;
            }
            return prev.map((r) => (r.id === row.id ? row : r));
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "responses", filter: `question_id=eq.${questionId}` },
        (payload) => {
          const deletedId = (payload.old as { id?: string })?.id;
          if (deletedId) {
            setResponses((prev) => prev.filter((r) => r.id !== deletedId));
          } else {
            load();
          }
        }
      )
      .subscribe();

    // Reconciliation poll: transitions OUT of public visibility (clean→block,
    // is_hidden flips) produce UPDATE events that RLS drops for anonymous
    // subscribers, so an anon stage would keep showing the stale row until
    // reload. A periodic refetch bounds that staleness to ≤45s. Cheap: one
    // indexed SELECT per question per interval.
    const reconcile = window.setInterval(load, 45_000);

    return () => {
      window.clearInterval(reconcile);
      supabase.removeChannel(channel);
    };
  }, [questionId, load, limit]);

  return { responses, reload: load };
}
